import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

interface Announcement {
    id: string;
    message: string;
    message_en: string | null;
    bg_color: string;
    text_color: string;
}

const AnnouncementBanner = () => {
    const { language } = useLanguage();
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const fetchAnnouncement = async () => {
            try {
                // Use the RPC function to get active announcement
                const { data, error } = await (supabase.rpc as any)("get_active_announcement");

                if (error) {
                    // Fallback to direct query if RPC doesn't exist yet
                    const { data: fallbackData } = await (supabase as any)
                        .from("announcements")
                        .select("id, message, message_en, bg_color, text_color")
                        .eq("is_active", true)
                        .or("expires_at.is.null,expires_at.gt." + new Date().toISOString())
                        .order("updated_at", { ascending: false })
                        .limit(1)
                        .single();

                    if (fallbackData) {
                        setAnnouncement(fallbackData);
                    }
                    return;
                }

                if (data && data.length > 0) {
                    setAnnouncement(data[0]);
                }
            } catch (err) {
                // Silently fail - announcements are optional
                console.log("Announcements not available:", err);
            }
        };

        fetchAnnouncement();

        // Poll every 5 minutes to check for new announcements
        const interval = setInterval(fetchAnnouncement, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    // Check localStorage for dismissed state
    useEffect(() => {
        if (announcement) {
            const dismissedId = localStorage.getItem("dismissed_announcement");
            if (dismissedId === announcement.id) {
                setDismissed(true);
            } else {
                setDismissed(false);
            }
        }
    }, [announcement]);

    const handleDismiss = () => {
        if (announcement) {
            localStorage.setItem("dismissed_announcement", announcement.id);
            setDismissed(true);
        }
    };

    if (!announcement || dismissed) {
        return null;
    }

    const displayMessage = language === "en" && announcement.message_en
        ? announcement.message_en
        : announcement.message;

    return (
        <div
            className="relative overflow-hidden py-2 px-4"
            style={{
                backgroundColor: announcement.bg_color,
                color: announcement.text_color
            }}
        >
            <div className="container mx-auto flex items-center justify-center">
                {/* Scrolling text */}
                <div className="flex-1 overflow-hidden whitespace-nowrap">
                    <div className="animate-marquee inline-block">
                        <span className="font-medium">
                            📢 {displayMessage}
                            <span className="mx-8">•</span>
                            📢 {displayMessage}
                            <span className="mx-8">•</span>
                            📢 {displayMessage}
                        </span>
                    </div>
                </div>

                {/* Dismiss button */}
                <button
                    onClick={handleDismiss}
                    className="ml-4 p-1 rounded-full hover:bg-white/20 transition-colors shrink-0"
                    style={{ color: announcement.text_color }}
                    aria-label="Dismiss announcement"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* Add marquee animation styles */}
            <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
        </div>
    );
};

export default AnnouncementBanner;
